import React from 'react';
import { observer } from 'mobx-react';
import { Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { IFieldLayout, INameObjectN, IShowForm } from '../../../interfaces';
import { useProfileFeatureFlags, useProfileIsRight, useSubscriberInfo } from '../../../hooks';
import { cleanObjectValue, dateText2Utc, objIsNotEmpty } from '../../../instruments';
import {
    CardInfoToolBar,
    CardMainLayout,
    Catcher,
    FieldInfoAdvanced,
    LinkComponent,
    MultiCheckboxShowField,
    ShowBooleanYNField,
    cardLayoutOptions,
} from '../../../Components';
import { DevicePopover } from '../../Card/Components';

export const ShowSubscriberInfo: React.FC<IShowForm> = observer(({ setEditMode }: IShowForm) => {
    const isSubscribersFeatures = useProfileFeatureFlags()?.subscribersFeatures;
    const isLord = useProfileIsRight('LORD');
    const info = useSubscriberInfo();
    const deviceInfo = info.deviceInfo;
    const isDeviceInfo = objIsNotEmpty(cleanObjectValue(deviceInfo || {}));

    const layout: IFieldLayout = {
        labelCol: {
            flex: '0 1 180px',
        },
    };

    let contentDevicePopover = null;

    if (deviceInfo) {
        contentDevicePopover = <DevicePopover info={deviceInfo} />;
    }

    return (
        <Catcher>
            <CardMainLayout
                idSpinners={['GET_SUBSCRIBER_V2']}
                name={info.imsi}
                columnOption={cardLayoutOptions.cardSubscribersInfo}
            >
                <FieldInfoAdvanced layout={layout} title={'IMSI'}>
                    {info.imsi}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'ICCID'}>
                    {info.iccId}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'MSISDN'}>
                    {info.msisdn}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'First seen'}>
                    {info.firstSeen ? dateText2Utc(info.firstSeen) : null}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'Last seen'}>
                    {info.lastSeen ? dateText2Utc(info.lastSeen) : null}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'Device manufacturer'}>
                    {info.deviceInfo?.manufacturer ? info.deviceInfo?.manufacturer : null}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'Device name'}>
                    {isDeviceInfo ? (
                        <Popover placement="right" title="Device Information" content={contentDevicePopover}>
                            <a>
                                {deviceInfo?.name || deviceInfo?.marketingName} <InfoCircleOutlined />
                            </a>
                        </Popover>
                    ) : null}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'Device type'}>
                    {info.deviceInfo?.deviceType ? info.deviceInfo?.deviceType : null}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'Active This Month'}>
                    <ShowBooleanYNField field={info.activeThisMonth} />
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'Card Name'}>
                    <LinkComponent currentListComponent={'CardList'} id={info.cardId} name={info.cardName} />
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'Companies'}>
                    {info.companies?.map((c: INameObjectN, i: number) => (
                        <LinkComponent
                            currentListComponent={'CompaniesList'}
                            id={c.id}
                            name={c.name}
                            key={`${c.id}-${i}`}
                        />
                    ))}
                </FieldInfoAdvanced>
                {isLord ? (
                    <FieldInfoAdvanced layout={layout} title={'Operator'}>
                        <LinkComponent
                            currentListComponent={'OperatorsList'}
                            id={info.operatorId}
                            name={info.operatorName}
                        />
                    </FieldInfoAdvanced>
                ) : null}
                <FieldInfoAdvanced layout={layout} title={'Statuses'}>
                    {info?.statuses?.join(', ')}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced layout={layout} title={'Migrate on prem'}>
                    <ShowBooleanYNField field={info.onPremMigrated} />
                </FieldInfoAdvanced>
                {isSubscribersFeatures ? (
                    <MultiCheckboxShowField layout={layout} title={'Features'} values={info.features} />
                ) : null}
                <CardInfoToolBar setEditMode={setEditMode} />
            </CardMainLayout>
        </Catcher>
    );
});
